/**
 * Mosaic card component.
 */
import ChannelUtils from '@shared/utils/channels';
import MediaProduction from '@shared/classes/mediaProduction';
import MosaicCardContainer from '@components/atoms/container/mosaicCardContainer';
import MosaicCardFocus from '@components/atoms/focus/mosaicCardFocus';
import MosaicCardImage from '@components/atoms/images/mosaicCardImage';
import MosaicCardInfo from './mosaicCardInfo';
import MosaicCardMask from '@components/atoms/mask/mosaicCardMask';
import RegularEvent from '@shared/classes/regularEvent';
import Setting from '@shared/classes/setting';
import closeMenu from '@shared/actions/menu/closeMenu';
import useList from '@shared/components/utils/hooks/useList';
import { magicRemoteSelector } from '@shared/store/slices/magicRemote.slice';
import { useCallback } from 'react';
import { useAppSelector } from '@shared/store/hooks';

/**
 * Props for the mosaic card component.
 */
interface Props {
  animatable: boolean;
  index: number;
  item: RegularEvent | MediaProduction | Setting;
  maxCols: number;
  minRows?: number;
  action: () => void;
  setAsset: (item: MediaProduction) => void;
}

/**
 * Mosaic card component. Represents a single mosaic card in the mosaic rail.
 *
 * @param {Props} props - Component props including item, index, and onClick handler
 * @returns {JSX.Element | null} Mosaic card element or null if not in view
 */
export default function MosaicCard({
  animatable = true,
  index,
  item,
  maxCols,
  minRows = 1,
  action,
  setAsset,
}: Props) {
  const { enabled } = useAppSelector(magicRemoteSelector);
  const { focused, listIndex } = useList(index);

  const currentEvaluationIndex = Math.floor(index / maxCols) + (minRows - 1);
  const evaluationIndex = Math.floor(listIndex / maxCols);

  // Data

  const animation =
    evaluationIndex <= currentEvaluationIndex
      ? 'opacity-transition-fast'
      : 'opacity-transition-fast-delayed';
  const displayCard = animatable
    ? evaluationIndex <= currentEvaluationIndex
    : true;
  const displayFocus = focused && index === listIndex && !enabled;
  const src = (item as Setting)?.image || (item as MediaProduction)?.photo;
  const upgradablePlan = (item as RegularEvent | MediaProduction)?.channel
    ? ChannelUtils.getUpgradablePlan(
        (item as RegularEvent | MediaProduction).channel,
        false,
        item.type
      )
    : null;

  // Actions

  const onMouseEnter = useCallback(() => {
    closeMenu();
    setAsset(item as MediaProduction);
  }, [item]);

  return (
    <MosaicCardContainer
      animation={animation}
      show={displayCard}
      onClick={action}
      onMouseEnter={onMouseEnter}
      id='mosaicCardContainer'
    >
      <MosaicCardImage src={src} />
      <MosaicCardMask />
      <MosaicCardInfo
        focused={displayFocus}
        item={item as MediaProduction | Setting}
        plan={upgradablePlan}
      />
      {displayFocus ? <MosaicCardFocus id={'mosaicCardFocus'} /> : null}
    </MosaicCardContainer>
  );
}
